<mat-card class="training-card">
  <mat-card-content>

    <!-- Header: where the rules stand for this builder. -->
    <div class="header-row">
      <div>
        <h3>Training</h3>
        <p class="hint">
          Teaching us how <strong>{{ selectedCompany?.name || 'this company' }}</strong>'s drawings are laid out.
          Pick the files that show something new, tell us in a sentence what was wrong, and start a run.
        </p>
      </div>
      <button mat-stroked-button (click)="loadData()" [disabled]="loading" matTooltip="Refresh">
        <mat-icon>refresh</mat-icon> Refresh
      </button>
    </div>

    <div *ngIf="loading" class="loading">
      <mat-spinner diameter="28"></mat-spinner>
    </div>

    <div *ngIf="!loading && loadError" class="load-error">
      <mat-icon color="warn">error_outline</mat-icon>
      <span>{{ loadError }}</span>
      <button mat-stroked-button (click)="loadData()">Retry</button>
    </div>

    <ng-container *ngIf="!loading && overview">

      <section class="overview">
        <p class="rules-line">
          {{ currentRulesSentence }}
          <ng-container *ngIf="overview.currentRules?.dateAdded">
            <span class="hint">Since {{ overview.currentRules?.dateAdded | date:'medium' }}.</span>
          </ng-container>
          <span *ngIf="overview.currentRules?.key" class="hint mono" [matTooltip]="overview.currentRules?.artifactId || ''">{{ overview.currentRules?.key }}</span>
          <a *ngIf="rulesVersions.length" class="toggle-link" (click)="showRulesHistory = !showRulesHistory">
            {{ showRulesHistory ? 'Hide history' : 'History' }}
          </a>
        </p>

        <table *ngIf="showRulesHistory" class="rules-history">
          <tr *ngFor="let v of rulesVersions">
            <td class="num">Version {{ v.version }}</td>
            <td class="mono hint" [matTooltip]="v.artifactId || ''">{{ v.key }}</td>
            <td>{{ v.dateAdded | date:'medium' }}</td>
            <td>
              <ng-container *ngIf="v.promotedFromRunNumber">From run {{ v.promotedFromRunNumber }}</ng-container>
              <ng-container *ngIf="v.promotedFromRunNumber && v.promotedFromAttempt && v.promotedFromAttempt > 1">, attempt {{ v.promotedFromAttempt }}</ng-container>
              <ng-container *ngIf="!v.promotedFromRunNumber">Uploaded</ng-container>
              <ng-container *ngIf="v.promotedBy"> by {{ v.promotedBy }}</ng-container>
            </td>
            <td><span *ngIf="v.isCurrent" class="chip good">in use</span></td>
          </tr>
        </table>

        <div class="active-run" *ngIf="activeRun">
          <mat-icon class="spin">autorenew</mat-icon>
          <div>
            <strong>Run {{ activeRun.runNumber }}: {{ describeStatus(activeRun) }}.</strong>
            <span class="hint">
              Requested {{ activeRun.dateRequested | date:'medium' }}<ng-container *ngIf="activeRun.requestedBy"> by {{ activeRun.requestedBy }}</ng-container>.
              Runs take between twenty minutes and an hour and a half; this page checks back every minute.
            </span>
          </div>
        </div>
      </section>

      <!-- Start a run: two doors into one form. -->
      <section class="start">
        <h4>Start a run</h4>

        <div class="entry" *ngIf="mode === 'idle'">
          <button mat-flat-button color="primary" (click)="beginNew()" [disabled]="!!activeRun">
            <mat-icon>school</mat-icon> Start a training run
          </button>
          <button mat-stroked-button *ngIf="fixableRuns.length" (click)="beginFix()" [disabled]="!!activeRun">
            <mat-icon>build</mat-icon> Fix a problem with an earlier training run
          </button>
          <span class="hint" *ngIf="activeRun">{{ busyMessage }}</span>
        </div>

        <ng-container *ngIf="mode !== 'idle'">

          <mat-form-field appearance="outline" class="wide-field" *ngIf="mode === 'fix'">
            <mat-label>Which run had the problem?</mat-label>
            <mat-select [value]="basedOn" (selectionChange)="baseOn($event.value)" [disabled]="starting">
              <mat-option *ngFor="let run of fixableRuns" [value]="run">{{ describeRunChoice(run) }}</mat-option>
            </mat-select>
            <mat-hint>Its files are carried over below. Add or remove any before you start.</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="wide-field">
            <mat-label>{{ feedbackPrompt }}</mat-label>
            <textarea matInput [(ngModel)]="feedbackText" rows="3" [disabled]="starting"
                      placeholder="A sentence is enough. For example: the pergola posts came through as walls on Lot 12."></textarea>
            <mat-hint>Optional. Plain words; we read it alongside the files.</mat-hint>
          </mat-form-field>

          <!-- Home, then its files -->
          <div class="picker">
            <div class="picker-head">
              <span class="picker-title">{{ mode === 'fix' ? 'Files for this run' : 'Which home?' }}</span>
              <mat-form-field appearance="outline" class="home-field">
                <mat-icon matPrefix>home</mat-icon>
                <input matInput placeholder="Type a home name" [(ngModel)]="homeQuery" (ngModelChange)="onHomeQueryChange()"
                       [matAutocomplete]="homeAuto" [disabled]="starting || homesLoading">
                <mat-autocomplete #homeAuto="matAutocomplete" (optionSelected)="chooseHome($event.option.value)">
                  <mat-option *ngFor="let home of filteredHomes" [value]="home">{{ home }}</mat-option>
                </mat-autocomplete>
              </mat-form-field>
            </div>

            <mat-progress-bar *ngIf="homesLoading || homeFilesLoading" mode="indeterminate" color="accent"></mat-progress-bar>

            <div *ngIf="!selectedHome && !homesLoading && !selected.size" class="hint empty">Choose a home to see its files.</div>
            <div *ngIf="selectedHome && !homeFilesLoading && !homeFiles.length" class="hint empty">
              {{ selectedHome }} has no files we can train from yet.
            </div>

            <div class="file-groups" *ngIf="selectedHome && homeFiles.length">
              <div class="file-group" *ngIf="masters.length">
                <div class="group-head">
                  <mat-checkbox color="primary" [checked]="allSelected(masters)" [indeterminate]="someSelected(masters)"
                                (change)="toggleAll(masters, $event.checked)" [disabled]="starting">
                    Master files
                  </mat-checkbox>
                  <span class="hint">{{ masters.length === 1 ? 'the floor plan file' : masters.length + ' floor plan files' }}</span>
                </div>
                <div class="group-row" *ngFor="let f of masters">
                  <mat-checkbox color="primary" [checked]="isHomeFileSelected(f)" (change)="toggleHomeFile(f, $event.checked)" [disabled]="starting">
                    {{ f.name }}
                  </mat-checkbox>
                  <span class="hint" *ngIf="latestUpload(f)">uploaded {{ latestUpload(f) | date:'short' }}</span>
                </div>
              </div>

              <div class="file-group" *ngIf="options.length">
                <div class="group-head">
                  <mat-checkbox color="primary" [checked]="allSelected(options)" [indeterminate]="someSelected(options)"
                                (change)="toggleAll(options, $event.checked)" [disabled]="starting">
                    Options
                  </mat-checkbox>
                  <span class="hint">{{ options.length === 1 ? 'one facade or option file' : options.length + ' facade and option files' }}</span>
                </div>
                <div class="group-row" *ngFor="let f of options">
                  <mat-checkbox color="primary" [checked]="isHomeFileSelected(f)" (change)="toggleHomeFile(f, $event.checked)" [disabled]="starting">
                    {{ f.name }}
                  </mat-checkbox>
                  <span class="hint" *ngIf="latestUpload(f)">uploaded {{ latestUpload(f) | date:'short' }}</span>
                </div>
              </div>
            </div>

            <div class="picker-foot">
              <a class="toggle-link" (click)="toggleAllUploads()">
                {{ showAllUploads ? 'Hide all uploads' : 'Choose from all uploads instead' }}
              </a>
            </div>

            <!-- Advanced: every upload for the company, any mix of homes -->
            <div class="all-uploads" *ngIf="showAllUploads">
              <div class="picker-head">
                <span class="picker-title">All uploads</span>
                <mat-form-field appearance="outline" class="filter-field">
                  <mat-icon matPrefix>search</mat-icon>
                  <input matInput placeholder="Filter by home" [(ngModel)]="ifcHomeFilter" (ngModelChange)="onIfcFilterChange()">
                </mat-form-field>
              </div>

              <mat-progress-bar *ngIf="ifcLoading" mode="indeterminate" color="accent"></mat-progress-bar>
              <div *ngIf="!ifcLoading && !ifcFiles.length" class="hint empty">No files to show.</div>

              <table class="file-table" *ngIf="ifcFiles.length">
                <tr *ngFor="let file of ifcFiles" [class.picked]="isSelected(file)" (click)="toggleFile(file, !isSelected(file))">
                  <td class="col-check">
                    <mat-checkbox color="primary" [checked]="isSelected(file)" (click)="$event.stopPropagation()"
                                  (change)="toggleFile(file, $event.checked)" [disabled]="starting"></mat-checkbox>
                  </td>
                  <td class="col-home">{{ file.home }}</td>
                  <td class="col-name">{{ file.name }}
                    <span class="chip small" *ngIf="isMaster(file)">master</span>
                  </td>
                  <td class="col-date hint">uploaded {{ file.dateAdded | date:'short' }}</td>
                </tr>
              </table>

              <mat-paginator [length]="ifcTotal" [pageSize]="ifcPageSize" [pageIndex]="ifcPageNumber - 1"
                             [pageSizeOptions]="[10, 25, 50]" (page)="onIfcPage($event)">
              </mat-paginator>
            </div>
          </div>

          <div class="chosen" *ngIf="selected.size">
            <span class="hint">Files for this run:</span>
            <span class="chip picked-chip" *ngFor="let f of selectedFiles">
              {{ f.home }} / {{ f.name }}
              <mat-icon (click)="toggleSource(f, false)">close</mat-icon>
            </span>
            <a class="toggle-link" (click)="clearSelection()">Clear</a>
          </div>

          <div class="start-actions">
            <button mat-flat-button color="primary" (click)="start()" [disabled]="!canStart" [class.button-spinner]="starting">
              <mat-icon>school</mat-icon> Start run
            </button>
            <button mat-button (click)="cancelStart()" [disabled]="starting">Cancel</button>
            <span class="hint" *ngIf="activeRun">{{ busyMessage }}</span>
            <span class="hint" *ngIf="!activeRun && !selected.size">Choose at least one file.</span>
            <span class="hint" *ngIf="!activeRun && selected.size">The latest upload of each file is used. Homes converted after the run passes use what it learned.</span>
          </div>
        </ng-container>
      </section>

      <!-- Runs -->
      <section class="runs">
        <h4>Runs <span class="hint" *ngIf="overview.runCount">{{ overview.runCount }} so far</span></h4>

        <p class="hint" *ngIf="!runs.length">No runs yet.</p>

        <table class="run-table" *ngIf="runs.length">
          <tr class="run-row" *ngFor="let run of runs" [class.open]="isSelectedRun(run)" (click)="openRun(run)">
            <td class="col-num">
              <strong>Run {{ run.runNumber }}</strong>
              <small *ngIf="run.attempts && run.attempts > 1">attempt {{ run.attempt }} of {{ run.attempts }}</small>
            </td>
            <td class="col-status">
              <span class="chip" [ngClass]="statusTone(run)">{{ describeStatus(run) }}</span>
            </td>
            <td class="col-sources">
              <span>{{ describeSources(run) }}</span>
              <small *ngIf="run.feedbackText" class="quote">"{{ run.feedbackText }}"</small>
            </td>
            <td class="col-counts hint">
              <span *ngIf="describeCounts(run)">{{ describeCounts(run) }}</span>
              <span *ngIf="run.warnings" class="warn"> · {{ run.warnings }} warning{{ run.warnings === 1 ? '' : 's' }}</span>
              <span *ngIf="run.errors" class="bad"> · {{ run.errors }} error{{ run.errors === 1 ? '' : 's' }}</span>
            </td>
            <td class="col-date hint">
              {{ (run.dateCompleted || run.dateRequested) | date:'medium' }}
              <small *ngIf="run.elapsedSeconds">{{ describeElapsed(run.elapsedSeconds) }}</small>
            </td>
            <td class="col-expand"><mat-icon>{{ isSelectedRun(run) ? 'expand_less' : 'expand_more' }}</mat-icon></td>
          </tr>
        </table>
      </section>

      <!-- Run detail -->
      <section class="detail" *ngIf="selectedRun as run">
        <div class="detail-head">
          <div>
            <h4>Run {{ run.runNumber }}<span *ngIf="run.attempts && run.attempts > 1" class="hint"> · attempt {{ run.attempt }} of {{ run.attempts }}</span></h4>
            <p class="status-line">
              <span class="chip" [ngClass]="statusTone(run)">{{ describeStatus(run) }}</span>
              <span class="hint" *ngIf="run.reason">{{ run.reason }}</span>
            </p>
          </div>
          <div class="detail-actions">
            <button mat-stroked-button *ngIf="canRerun(run)" (click)="rerun(run)" [disabled]="acting"
                    matTooltip="Start a new attempt from the same files and message">
              <mat-icon>replay</mat-icon> Run again
            </button>
            <button mat-flat-button color="primary" *ngIf="canPromote(run)" (click)="promote(run)" [disabled]="acting">
              <mat-icon>publish</mat-icon> Promote
            </button>
            <button mat-stroked-button color="warn" *ngIf="canAbandon(run)" (click)="abandon(run)" [disabled]="acting"
                    matTooltip="Give up on this run so the next one can start">
              <mat-icon>block</mat-icon> Abandon
            </button>
            <button mat-icon-button (click)="closeRun()" matTooltip="Close"><mat-icon>close</mat-icon></button>
          </div>
        </div>

        <div *ngIf="detailLoading" class="loading"><mat-spinner diameter="24"></mat-spinner></div>
        <div *ngIf="detailError" class="load-error"><mat-icon color="warn">error_outline</mat-icon><span>{{ detailError }}</span></div>

        <ng-container *ngIf="!detailLoading && !detailError">

          <p class="hint" *ngIf="run.status === TrainingRunStatus.NotRequested">
            The files and message were saved but the run never started. Run again to send it through.
          </p>
          <p class="hint" *ngIf="run.status === TrainingRunStatus.Error">
            The run was lost before it reached a verdict. Run again to send it through.
          </p>
          <p class="hint" *ngIf="run.status === TrainingRunStatus.Abandoned">
            Someone gave up on this run before it finished. Its files and message are kept; run again to send it through.
          </p>
          <p class="hint" *ngIf="run.status === TrainingRunStatus.Passed && !run.promotedToRulesVersion">
            This run passed but its rules are not in use<ng-container *ngIf="hasErrorFlag(run)">, held back by an error below</ng-container>.
          </p>
          <p class="hint" *ngIf="run.promotedToRulesVersion">
            Its rules became version {{ run.promotedToRulesVersion }}<ng-container *ngIf="run.datePromoted"> on {{ run.datePromoted | date:'medium' }}</ng-container><ng-container *ngIf="run.promotedBy"> ({{ run.promotedBy }})</ng-container>.
          </p>

          <dl class="facts">
            <dt>Requested</dt>
            <dd>{{ run.dateRequested | date:'medium' }}<ng-container *ngIf="run.requestedBy"> by {{ run.requestedBy }}</ng-container></dd>
            <ng-container *ngIf="run.dateStarted"><dt>Started</dt><dd>{{ run.dateStarted | date:'medium' }}</dd></ng-container>
            <ng-container *ngIf="run.dateCompleted"><dt>Finished</dt><dd>{{ run.dateCompleted | date:'medium' }}<ng-container *ngIf="run.elapsedSeconds">, {{ describeElapsed(run.elapsedSeconds) }}</ng-container></dd></ng-container>
            <ng-container *ngIf="run.coldStart"><dt>Starting point</dt><dd>Situ's starting rules; this builder had none of its own yet.</dd></ng-container>
            <dt>Files</dt>
            <dd>
              <div *ngFor="let s of run.sources">{{ describeSource(s) }}<span class="chip small" *ngIf="s.isMaster">master</span></div>
            </dd>
            <ng-container *ngIf="run.feedbackText"><dt>Your message</dt><dd class="quote">"{{ run.feedbackText }}"</dd></ng-container>
            <ng-container *ngIf="run.trainingRunKey || run.trainingDataKey">
              <dt>Reference</dt>
              <dd class="mono">
                <span *ngIf="run.trainingRunKey" [matTooltip]="run.trainingRunArtifactId || ''">{{ run.trainingRunKey }}</span>
                <span *ngIf="run.trainingRunKey && run.trainingDataKey"> · </span>
                <span *ngIf="run.trainingDataKey" [matTooltip]="run.trainingDataArtifactId || ''">{{ run.trainingDataKey }}</span>
              </dd>
            </ng-container>
          </dl>

          <!-- Per-file results -->
          <ng-container *ngIf="run.files?.length">
            <h5>What each file taught</h5>
            <table class="file-results">
              <tr *ngFor="let f of run.files">
                <td class="col-path">{{ f.path }}</td>
                <td class="col-fstatus">
                  <span class="chip" [ngClass]="f.status === TrainingFileStatus.Trained ? 'good' : (f.status === TrainingFileStatus.Failed ? 'bad' : 'muted')">{{ describeFileStatus(f.status) }}</span>
                </td>
                <td class="col-learned hint">
                  <ng-container *ngIf="f.status === TrainingFileStatus.Trained">
                    {{ f.newRules || 0 }} new, {{ f.changedRules || 0 }} changed<ng-container *ngIf="describeValidation(f.validation)">; {{ describeValidation(f.validation) }}</ng-container>
                  </ng-container>
                  <ng-container *ngIf="f.status !== TrainingFileStatus.Trained && f.reason">{{ f.reason }}</ng-container>
                </td>
                <td class="col-elapsed hint">{{ describeElapsed(f.elapsedSeconds) }}</td>
              </tr>
            </table>
          </ng-container>

          <!-- Flags -->
          <ng-container *ngIf="flagsFor(run).length">
            <h5>Worth knowing</h5>
            <ul class="flags">
              <li *ngFor="let flag of flagsFor(run)" [class.bad]="flag.severity === TrainingFlagSeverity.Error">
                <mat-icon>{{ flag.severity === TrainingFlagSeverity.Error ? 'error_outline' : 'warning_amber' }}</mat-icon>
                <div>
                  <span>{{ flag.message }}</span>
                  <small *ngIf="flag.file" class="hint">{{ flag.file }}</small>
                  <small *ngIf="isSituUser" class="staff-only mono">{{ flag.code }}<ng-container *ngIf="flag.evidence"> · {{ flag.evidence }}</ng-container></small>
                </div>
              </li>
            </ul>
          </ng-container>

          <!-- Feedback response -->
          <ng-container *ngIf="run.feedbackResponse?.length">
            <h5>What we did with your message</h5>
            <table class="feedback-table">
              <tr *ngFor="let item of run.feedbackResponse">
                <td class="col-quote quote">"{{ item.quote }}"</td>
                <td class="col-action">
                  <span *ngIf="item.action">{{ item.action }}</span>
                  <small class="hint" *ngIf="item.explanation">{{ item.explanation }}</small>
                  <small class="hint" *ngIf="item.file || item.trainedFile">{{ item.trainedFile || item.file }}</small>
                  <small *ngIf="isSituUser" class="staff-only mono">{{ item.id }}<ng-container *ngIf="item.kind"> · {{ item.kind }}</ng-container><ng-container *ngIf="item.status"> · {{ item.status }}</ng-container></small>
                </td>
              </tr>
            </table>
          </ng-container>

          <!-- Situ-only: provenance and evidence -->
          <ng-container *ngIf="isSituUser">
            <div class="staff-divider"><span class="staff-note">Visible to Situ staff only.</span></div>

            <dl class="facts mono">
              <ng-container *ngIf="run.llm"><dt>Model</dt><dd>{{ run.llm.modelId || run.llm.modelPath }}<ng-container *ngIf="run.llm.source"> · {{ run.llm.source }}</ng-container><ng-container *ngIf="run.llm.endpoint"> · {{ run.llm.endpoint }}</ng-container></dd></ng-container>
              <ng-container *ngIf="run.rulesIn"><dt>Rules in</dt><dd>{{ run.rulesIn }}</dd></ng-container>
              <ng-container *ngIf="run.payloadVersion"><dt>Payload</dt><dd>{{ run.payloadVersion }}</dd></ng-container>
              <ng-container *ngFor="let f of run.files">
                <ng-container *ngIf="f.processor || f.detected || f.sha256">
                  <dt>{{ f.path }}</dt>
                  <dd><ng-container *ngIf="f.processor">{{ f.processor }}</ng-container><ng-container *ngIf="f.detected"> · {{ f.detected }}</ng-container><ng-container *ngIf="f.session !== undefined && f.session !== null"> · session {{ f.session }}</ng-container><ng-container *ngIf="f.sha256"> · {{ f.sha256 }}</ng-container></dd>
                </ng-container>
              </ng-container>
            </dl>

            <ng-container *ngIf="run.evidenceFiles?.length">
              <h5>Evidence</h5>
              <ul class="evidence">
                <li *ngFor="let path of run.evidenceFiles">
                  <a class="toggle-link mono" (click)="downloadEvidence(run, path)" [class.busy]="downloading === path">{{ path }}</a>
                  <mat-spinner *ngIf="downloading === path" diameter="14"></mat-spinner>
                </li>
              </ul>
            </ng-container>
          </ng-container>

        </ng-container>
      </section>

    </ng-container>
  </mat-card-content>
</mat-card>
